import { useRef, useState } from 'react';
import { useBillingClient } from 'client/client-hook';
import useAuth from 'hooks/useAuth';
import { getFreezeErrorMessage } from './formatters';
import { createClientIdempotencyKey } from './storage';
import { useSubscriptionFreeze } from './SubscriptionFreezeContext';
import { FreezeQuote } from './types';

// Parts of billing-manager's refusals when a confirmation races a change.
const BILLING_CHANGED_ERROR = 'billing state changed';
// A pause already exists (another tab, or this one's lost reply): it is open either way.
const ALREADY_OPEN_ERRORS = ['already open', 'idempotency key was used'];

export type PauseResult = { outcome: 'paused' } | { outcome: 'billingChanged' } | { outcome: 'failed'; message: string };

/** Starts the pause the customer reviewed; the freeze status is refreshed before it reports success. */
export function usePauseCreate() {
  const { user } = useAuth() as any;
  const userId: string = user?.tcId || '';
  const getBillingClient = useBillingClient();
  const { refresh } = useSubscriptionFreeze();
  // One per review, so a retry after a lost reply replays the same request instead of a second pause.
  const idempotencyKey = useRef(createClientIdempotencyKey());
  const [busy, setBusy] = useState(false);

  const pause = async (quote: FreezeQuote): Promise<PauseResult> => {
    setBusy(true);
    try {
      const client = await getBillingClient();
      await client.createSubscriptionFreeze({
        userId,
        snapshotHash: quote.snapshotHash,
        idempotencyKey: idempotencyKey.current
      });
      await refresh();
      return { outcome: 'paused' };
    } catch (cause) {
      const message = getFreezeErrorMessage(cause);
      if (ALREADY_OPEN_ERRORS.some((part) => message.includes(part))) {
        await refresh();
        return { outcome: 'paused' };
      }
      return message.includes(BILLING_CHANGED_ERROR) ? { outcome: 'billingChanged' } : { outcome: 'failed', message };
    } finally {
      setBusy(false);
    }
  };

  return { pause, busy };
}
